import { VisitEventStatusEnum, VisitResourceErrandStatusEnum, VisitResourceVirtualProviderStatusEnum } from "~/graphql/__generated__/graphql";
import { StatusBgColors, StatusBgColorsDark, VisitStatuses } from "@caredna-marlin/utils/client";
import { startCase } from "lodash";

// 700 Definition for text/borders
export const StatusForegroundMap: {[key: string]: string} = {
  [VisitStatuses.scheduled]: StatusBgColorsDark.scheduled,
  [VisitStatuses.en_route]: 'white',
  [VisitStatuses.arrived]: StatusBgColorsDark.arrived,
  [VisitStatuses.in_progress]: StatusBgColorsDark.in_progress,
  [VisitStatuses.completed]: StatusBgColorsDark.completed,
  [VisitStatuses.canceled]: StatusBgColorsDark.canceled,
};

// 100 Definition for background
export const StatusBgMap: {[key: string]: string} = {
  [VisitStatuses.scheduled]: StatusBgColors.scheduled,
  [VisitStatuses.en_route]: StatusBgColors.en_route,
  [VisitStatuses.arrived]: StatusBgColors.arrived,
  [VisitStatuses.in_progress]: StatusBgColors.in_progress,
  [VisitStatuses.completed]: StatusBgColors.completed,
  [VisitStatuses.canceled]: StatusBgColors.canceled,
};

export const StatusPalate = {
  [VisitStatuses.completed]: [
    "#e7fae8",
    StatusBgColors.scheduled,
    "#7de486",
    "#48d954",
    "#27bb33",
  ],
  [VisitStatuses.scheduled]: [
    StatusBgColors.completed, // Scheduled visit
    "#f3f3f3", // Drive time
    "#bebebe", // Cancelled
  ],
  [VisitStatuses.canceled]: [
    "#fff5f2",
    "#ffc7b2",
    "#ff9972",
    "#ff6a32",
    "#f24200",
  ],
}

export const VisitTypeColors = [
  "#8AF0E1",
  "#80E0D3",
  "#95D0CE",
  "#C8F3F1",
  "#97D7E3",
  "#80A7A5",
  "#DBF9FF",
  "#80E3DF",
  "#91E4E2",
  "#97D7E3",
  "#96DAE3",
];

// The VisitEvent:og's `event` field is untyped; while we currently expect it to hold a visit status or visit
// resource status, it's a string in the database because we decided to leave it flexible for additional future uses.
// Who knows what might end up there in the future; therefore "string" is part of this union type, and we
// need a `default` in the switch below to handle unknown values.
type DisplayStatus = `${VisitStatuses}` | `${VisitResourceVirtualProviderStatusEnum}` | string | null;

export function displayStatus(status: DisplayStatus, about: 'visit' | 'visit_resource'): string {
  switch (status) {
    case 'scheduled':
      return 'Scheduled';
    case 'complete':
      return about === 'visit_resource' ? 'Call Completed' : 'Completed';
    case 'en_route':
      return 'En Route';
    case 'arrived':
      return 'Arrived';
    case 'in_progress':
      return 'In Progress';
    case 'canceled':
      return 'Canceled';
    case 'notified':
      return 'Notified';
    case 'joined':
      return 'Joined';
    case null:
      return 'No Status';
    default:
      return startCase(status);
  }
}

// Causes TypeScript errors if A and B ever have different members.
export type MatchingStringEnums<A extends string, B extends string> =
  [A] extends [B]
    ? [B] extends [A]
      ? A
      : { error: "String enums have diverged"; missingFromFirst: Exclude<B, A> }
    : { error: "String enums have diverged"; missingFromSecond: Exclude<A, B> };

export type SharedVisitCalendarStatus = MatchingStringEnums<
  `${VisitEventStatusEnum}`,
  `${VisitResourceErrandStatusEnum}`
>;

export type VisitResourceStatusSource = {
  errandStatus?: VisitResourceErrandStatusEnum | null;
  visit: { eventStatus: VisitEventStatusEnum };
};

export const visitResourceStatus = (resource: VisitResourceStatusSource): VisitEventStatusEnum => {
  if (resource.visit.eventStatus === VisitEventStatusEnum.Canceled && resource.errandStatus === VisitResourceErrandStatusEnum.Scheduled) {
    return VisitEventStatusEnum.Canceled;
  }
  return (resource.errandStatus ?? resource.visit.eventStatus) satisfies SharedVisitCalendarStatus as VisitEventStatusEnum;
};

export const calculateInfoColor = (visit: { eventStatus: VisitEventStatusEnum }) => {
  return {
    text: StatusForegroundMap[visit.eventStatus],
    backgroundColor: StatusBgMap[visit.eventStatus],
  };
};
